import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Link } from "react-router";
import { Button } from "@/components/design/Button";
import { Select } from "@/components/design/Select";
import { AccessGroupsDocument, AccessGroupInspectionDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { accessScopeLabel } from "@/lib/access-scope-label";
import { groupLabel } from "./access-group-labels";
import { roleLabel } from "./access-role-labels";
import { permissionLabel } from "./access-permission-labels";

export function AccessGroupInspection() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [groupId, setGroupId] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const groups = useQuery(AccessGroupsDocument, { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true });
  const inspection = useQuery(AccessGroupInspectionDocument, { variables: { groupId }, skip: !groupId, fetchPolicy: "network-only", notifyOnNetworkStatusChange: true });
  const reload = () => { setFailure(null); void groups.refetch().catch(() => {}); if (groupId) void inspection.refetch().catch(() => {}); };
  const error = groups.error ?? inspection.error;
  async function loadMore() {
    if (!groups.data?.accessGroups.nextAfter || groups.loading) return;
    try {
      await groups.fetchMore({ variables: { after: groups.data.accessGroups.nextAfter }, updateQuery(previous, { fetchMoreResult }) {
        const page = fetchMoreResult.accessGroups;
        const items = new Map(previous.accessGroups.groups.map(group => [group.id, group]));
        page.groups.forEach(group => items.set(group.id, group));
        return { ...previous, accessGroups: { ...page, groups: [...items.values()] } };
      } });
      setFailure(null);
    } catch (cause) { setFailure(userErrorMessage(intl, cause)); }
  }
  return <div className="space-y-4">
    <label className="block"><span className="smycles-field-label">{t("admin.inspection.group")}</span>
      <Select id="inspection-group-select" value={groupId} onChange={setGroupId} options={(groups.data?.accessGroups.groups ?? [])
        .map(group => ({ value: group.id, label: groupLabel(intl, group) })).sort((a, b) => a.label.localeCompare(b.label, intl.locale))} /></label>
    {groups.data?.accessGroups.hasNextPage && <Button variant="secondary" disabled={groups.loading} onClick={() => void loadMore()}>{t("common.loadMore")}</Button>}
    {(groups.loading || inspection.loading) && <p role="status" className="text-smycles-body text-ink-muted">{t("common.loading")}</p>}
    {(error || failure) && <div className="space-y-2"><p role="alert" className="text-smycles-body">{failure ?? userErrorMessage(intl, error)}</p>
      <Button variant="secondary" onClick={reload}>{t("common.tryAgain")}</Button></div>}
    {groupId && !inspection.loading && !error && inspection.data && <div id="inspection-group-result" className="space-y-4 border-t border-ink/10 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link className="text-smycles-body underline underline-offset-2" to={`/admin/settings?section=accessGroups&groupId=${encodeURIComponent(groupId)}`}>{t("admin.inspection.openGroup")}</Link>
        <Button id="inspection-group-refresh" variant="secondary" size="compact" onClick={reload}>{t("admin.inspection.refresh")}</Button>
      </div>
      <ul className="divide-y divide-ink/10">{inspection.data.accessGroupRoleAssignments.map(assignment => {
        const role = inspection.data.accessRoles.roles.find(role => role.id === assignment.roleId);
        return <li key={assignment.id} className="space-y-2 py-4">
          <Link className="text-smycles-body font-medium underline underline-offset-2" to={`/admin/settings?section=accessRoles&roleId=${encodeURIComponent(assignment.roleId)}`}>{role ? roleLabel(intl, role) : assignment.roleName}</Link>
          <p className="text-smycles-meta text-ink-muted">{accessScopeLabel({ ...assignment.scope,
            groupName: assignment.scope.groupId ? groupLabel(intl, { id: assignment.scope.groupId, name: assignment.scope.groupName ?? assignment.scope.groupId }) : null }, intl)}</p>
          <ul className="space-y-1 text-smycles-body">{role?.permissions.map(permission => <li key={permission.code}>{permissionLabel(intl, permission.code, permission.description)}</li>)}</ul>
        </li>;
      })}</ul>
      {!inspection.data.accessGroupRoleAssignments.length && <p className="text-smycles-body text-ink-muted">{t("admin.inspection.noRoles")}</p>}
      <p className="text-smycles-meta text-ink-muted">{t("admin.inspection.groupHint")}</p>
    </div>}
  </div>;
}
